import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { useAuth } from '../auth/AuthProvider';
import { apiFetch, ApiError } from '../lib/api';

interface ManagedUser {
  user_id: string;
  email: string;
  full_name: string | null;
  role: 'admin' | 'member';
  is_active: boolean;
  last_seen_at: string | null;
}

export function AdminUsers() {
  const { user } = useAuth();
  const [users, setUsers] = useState<ManagedUser[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  // Invite form.
  const [email, setEmail] = useState('');
  const [fullName, setFullName] = useState('');
  const [role, setRole] = useState<'admin' | 'member'>('member');

  const load = useCallback(async () => {
    setError(null);
    try {
      const data = await apiFetch<ManagedUser[]>('/api/admin/users');
      setUsers(data);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Failed to load users');
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  async function run(body: Record<string, unknown>, successMsg: string) {
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      await apiFetch('/api/admin/users', { method: 'POST', body: JSON.stringify(body) });
      setNotice(successMsg);
      await load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Operation failed');
    } finally {
      setBusy(false);
    }
  }

  async function onInvite(e: FormEvent) {
    e.preventDefault();
    await run(
      { op: 'invite', email: email.trim(), full_name: fullName.trim(), role },
      `Invitation sent to ${email.trim()}.`,
    );
    setEmail('');
    setFullName('');
    setRole('member');
  }

  return (
    <section className="page">
      <h1>Users</h1>
      <p className="muted">
        Invite teammates and manage access. Invited users receive an email that signs them in — they
        then use the six-digit code or magic link to sign in any time. No passwords.
      </p>

      <form onSubmit={onInvite} className="invite-form">
        <div className="invite-row">
          <label>
            Email
            <input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </label>
          <label>
            Full name (optional)
            <input type="text" value={fullName} onChange={(e) => setFullName(e.target.value)} />
          </label>
          <label>
            Role
            <select value={role} onChange={(e) => setRole(e.target.value as 'admin' | 'member')}>
              <option value="member">member</option>
              <option value="admin">admin</option>
            </select>
          </label>
          <button type="submit" disabled={busy}>
            {busy ? 'Working…' : 'Send invite'}
          </button>
        </div>
      </form>

      {error && <p className="form-error" role="alert">{error}</p>}
      {notice && <p className="form-success" role="status">{notice}</p>}

      <table className="users-table">
        <thead>
          <tr>
            <th>Email</th>
            <th>Name</th>
            <th>Role</th>
            <th>Status</th>
            <th>Last seen</th>
            <th aria-label="actions" />
          </tr>
        </thead>
        <tbody>
          {users === null && (
            <tr>
              <td colSpan={6} className="muted">Loading…</td>
            </tr>
          )}
          {users?.map((u) => {
            const isSelf = u.user_id === user?.id;
            return (
              <tr key={u.user_id} className={u.is_active ? '' : 'row-inactive'}>
                <td>{u.email}{isSelf && <span className="muted"> (you)</span>}</td>
                <td>{u.full_name ?? '—'}</td>
                <td>{u.role}</td>
                <td>{u.is_active ? 'active' : 'inactive'}</td>
                <td className="muted">
                  {u.last_seen_at ? new Date(u.last_seen_at).toLocaleString() : 'never'}
                </td>
                <td className="row-actions">
                  <button
                    type="button"
                    disabled={busy || isSelf}
                    onClick={() =>
                      void run(
                        { op: 'update', user_id: u.user_id, role: u.role === 'admin' ? 'member' : 'admin' },
                        `${u.email} is now ${u.role === 'admin' ? 'member' : 'admin'}.`,
                      )
                    }
                  >
                    Make {u.role === 'admin' ? 'member' : 'admin'}
                  </button>
                  <button
                    type="button"
                    disabled={busy || isSelf}
                    onClick={() =>
                      void run(
                        { op: 'update', user_id: u.user_id, is_active: !u.is_active },
                        `${u.email} ${u.is_active ? 'deactivated' : 'activated'}.`,
                      )
                    }
                  >
                    {u.is_active ? 'Deactivate' : 'Activate'}
                  </button>
                  <button
                    type="button"
                    disabled={busy}
                    onClick={() => void run({ op: 'resend', email: u.email }, `Sign-in email resent to ${u.email}.`)}
                  >
                    Resend
                  </button>
                  <button
                    type="button"
                    className="danger"
                    disabled={busy || isSelf}
                    onClick={() => {
                      if (window.confirm(`Delete ${u.email}? This cannot be undone.`)) {
                        void run({ op: 'delete', user_id: u.user_id }, `${u.email} deleted.`);
                      }
                    }}
                  >
                    Delete
                  </button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </section>
  );
}
